import React from 'react';

import { useGetList } from '@vlabs/api/artemy/client';
import type { HandlerMatchPolicyItem, HandlerRecord } from '@vlabs/api/artemy/model';
import { EVENT_OPTIONS } from '@vlabs/api-bindings/src/constants';

import {
  ViewCard, ViewRow, ViewRowSection, ViewRowTitle,
} from '../components';
import { useHandlerViewPageTranslation } from '../i18n';
import { optionLabel } from './options';

interface MatchSectionProps {
  handler: HandlerRecord;
}

interface MatchPolicyCardProps {
  item: HandlerMatchPolicyItem;
  index: number;
}

function MatchPolicyCard({ item, index }: MatchPolicyCardProps): React.JSX.Element {
  const { t: tView } = useHandlerViewPageTranslation();
  const listId = item.candidates?.list_id;
  const { data: list } = useGetList(listId ?? '', {
    query: { enabled: !!listId },
  });

  const originLabel = item.candidates?.origin
    ? optionLabel(EVENT_OPTIONS, item.candidates.origin)
    : undefined;

  const hasAdditionalParams = (
    (item.limit !== undefined && item.limit !== null)
    || (item.threshold !== undefined && item.threshold !== null)
  );

  return (
    <ViewCard
      header={(
        <ViewRowTitle label={tView('match.label')}>
          {item.label}
        </ViewRowTitle>
      )}
      value={`match-${index}`}
    >
      {originLabel
        ? (
          <ViewRow label={tView('match.identify_among')}>
            {originLabel}
          </ViewRow>
        )
        : null}
      {!!listId && (
        <React.Fragment>
          <ViewRowSection label={tView('match.search_by')} />
          <ViewRow label={tView('match.list')}>
            {list?.user_data ?? listId}
          </ViewRow>
        </React.Fragment>
      )}
      {!!hasAdditionalParams && (
        <React.Fragment>
          <ViewRowSection label={tView('match.additional_search_params')} />
          {item.limit !== undefined && item.limit !== null
            ? (
              <ViewRow label={tView('match.max_candidates')}>
                {String(item.limit)}
              </ViewRow>
            )
            : null}
          {item.threshold !== undefined && item.threshold !== null
            ? (
              <ViewRow label={tView('match.threshold')}>
                {String(item.threshold)}
              </ViewRow>
            )
            : null}
        </React.Fragment>
      )}
    </ViewCard>
  );
}

function MatchSection({ handler }: MatchSectionProps): React.JSX.Element | null {
  const matchPolicy = handler.policies.match_policy ?? [];
  if (matchPolicy.length === 0) return null;

  return (
    <React.Fragment>
      {matchPolicy.map((item, index) => (
        <MatchPolicyCard
          index={index}
          item={item}
          // eslint-disable-next-line react/no-array-index-key
          key={`${item.label ?? ''}-${index}`}
        />
      ))}
    </React.Fragment>
  );
}

export { MatchSection };
